import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function MergeTripsDialog({ trips, onClose, onMerged }) {
  const [targetId, setTargetId] = useState(trips[0]?.id || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/trips/merge", {
        target_trip_id: targetId,
        source_trip_ids: trips.filter((t) => t.id !== targetId).map((t) => t.id),
      });
      toast.success(`Merged ${data.merged_trip_names.length + 1} trips · ${data.moved_reservations} reservations moved`);
      onMerged(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Merge failed");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-sand-300" data-testid="merge-trips-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-2xl tracking-tight">
            Merge {trips.length} trips
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-sand-600">
            Pick the trip to keep. Its name stays; all reservations from the other trips move
            into it, dates widen to cover everything, and the other trips are deleted.
          </p>
          <div className="space-y-2">
            {trips.map((t) => (
              <label
                key={t.id}
                className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${targetId === t.id ? "border-ember bg-ember/5" : "border-sand-200 hover:border-sand-400"}`}
                data-testid={`merge-target-option-${t.id}`}
              >
                <input
                  type="radio"
                  name="merge-target"
                  checked={targetId === t.id}
                  onChange={() => setTargetId(t.id)}
                  className="accent-ember"
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{t.name}</span>
                  <span className="block font-mono text-[11px] text-sand-500">
                    {t.start_date ? `${t.start_date}${t.end_date && t.end_date !== t.start_date ? ` → ${t.end_date}` : ""}` : "unscheduled"}
                    {" · "}{t.res_total} reservation{t.res_total !== 1 ? "s" : ""}
                  </span>
                </span>
                {targetId === t.id && (
                  <span className="font-mono text-[10px] uppercase text-ember">keeps name</span>
                )}
              </label>
            ))}
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="btn-swiss btn-ghost text-sm" data-testid="merge-cancel-btn">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !targetId}
            className="btn-swiss btn-primary text-sm disabled:opacity-50"
            data-testid="merge-confirm-btn"
          >
            {busy ? "Merging…" : "Merge trips"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
